import React, { memo, useEffect, useMemo } from "react";

// internal deps
import { Ledger } from "@swiggy-private/dls-web-recipe-common-web/dist/components/Ledger";
import { ITab, TransactionModes } from "@swiggy-private/dls-web-recipe-common-web/dist/components/Ledger/types";
import { format, isEmpty, isEqual } from "@swiggy-private/js-utils";
import { LottieAnimation } from "@swiggy-private/core-ui";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as SparklesIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/sparkle/16";

// test ids
import { DINE_CASH_POOLING_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";

// styles
import {
    SenderPopupContainer,
    SenderPopupText,
    LottieExpandWrapper,
    LedgerContainer,
    LedgerScrollable,
    SenderPopupHeaderContent,
    MirroredIconWrapper,
} from "./styles";

// types
import { IPopupAsset, ISender } from "../../types";

// constants
import { ALL_TAB_NAME, POOLING_LEDGER_POPUP_TITLE } from "../../constants";

interface IPoolingLedgerProps {
    senders?: ISender[];
    popupAsset?: IPopupAsset;
    maxPoolAmountReached: boolean;
    onLedgerImpression?: (maxPoolAmountReached: boolean, totalAmountReceived: number, senders?: ISender) => void;
    onLedgerHeaderImpression?: VoidFunction;
}

const PoolingLedger = ({
    senders,
    popupAsset,
    maxPoolAmountReached,
    onLedgerImpression,
    onLedgerHeaderImpression,
}: IPoolingLedgerProps) => {
    const items = useMemo(
        () =>
            (senders ?? []).map((sender) => ({
                date: "",
                amount: sender.pledgedAmount,
                title: sender.name,
                description: "",
                mode: TransactionModes.CREDIT,
                showAvatar: true,
            })),
        [senders],
    );

    const totalAmountReceived = useMemo(() => {
        return senders?.reduce((acc, sender) => acc + sender.pledgedAmount, 0) ?? 0;
    }, [senders]);

    const tabs: ITab[] = useMemo(() => [{ name: ALL_TAB_NAME, tabIndex: 0, items }], [items]);

    const senderCount = senders?.length ?? 0;
    const popupTitle = format(POOLING_LEDGER_POPUP_TITLE, senderCount, !isEqual(senderCount, 1) ? "s" : "");
    const popupLottieId = popupAsset?.lottieId;

    useEffect(() => {
        if (isEmpty(senders)) return;
        const lastSender = senders[senders.length - 1];
        onLedgerImpression?.(maxPoolAmountReached, totalAmountReceived, lastSender);
    }, [maxPoolAmountReached, totalAmountReceived, senders, onLedgerImpression]);

    useEffect(() => {
        if (maxPoolAmountReached) onLedgerHeaderImpression?.();
    }, [maxPoolAmountReached, onLedgerHeaderImpression]);

    if (isEmpty(senders)) {
        return null;
    }

    return (
        <SenderPopupContainer data-testid={DINE_CASH_POOLING_TEST_IDS.SENDER_POPUP}>
            {!maxPoolAmountReached && !isEmpty(popupTitle) && (
                <SenderPopupHeaderContent>
                    <MirroredIconWrapper>
                        <GenericIcon
                            fillColor="text_inverse_Highest_Emphasis"
                            strokeColor="text_inverse_Highest_Emphasis"
                            Icon={SparklesIcon}
                        />
                    </MirroredIconWrapper>
                    <SenderPopupText variant="V2.body1.neutral.medium" color="text_inverse_Highest_Emphasis">
                        {popupTitle}
                    </SenderPopupText>
                    <GenericIcon
                        fillColor="text_inverse_Highest_Emphasis"
                        strokeColor="text_inverse_Highest_Emphasis"
                        Icon={SparklesIcon}
                    />
                </SenderPopupHeaderContent>
            )}
            {maxPoolAmountReached && !isEmpty(popupLottieId) && (
                <LottieExpandWrapper>
                    <LottieAnimation
                        fileName={popupLottieId}
                        visible={true}
                        autoplay={true}
                        loop={false}
                        playLottie={true}
                        playIfVisible={false}
                        backwardSpeed={1}
                        forwardSpeed={1}
                    />
                </LottieExpandWrapper>
            )}
            <LedgerContainer>
                <LedgerScrollable>
                    <Ledger tabs={tabs} canShowLoadMore={false} currentTab={0} />
                </LedgerScrollable>
            </LedgerContainer>
        </SenderPopupContainer>
    );
};

export default memo(PoolingLedger);
